import React, { useContext, useEffect, useState, useCallback } from "react";
import Context from "../../../Context";
import { 
  ArrowDownLeft, 
  ArrowUpRight, 
  RefreshCw, 
  Calendar, 
  Search, 
  X, 
  Filter,
  Wallet,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  Eye,
  EyeOff,
  Tag,
  Check
} from "lucide-react";
import { auth, db } from "../../../firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { apiFetch } from "../../../utils/api";
import styles from "./Transactions.module.scss";

interface Transaction {
  id: string;
  name: string;
  amount: number;
  date: string;
  datetime?: string;
  authorized_date?: string;
  category: string[] | null;
  merchant_name?: string;
  pending?: boolean;
  account_id: string;
  personal_finance_category?: {
    primary?: string;
    detailed?: string;
    confidence_level?: string;
  };
}

interface Account {
  account_id: string;
  name: string;
  official_name?: string;
  type: string;
  subtype?: string;
  mask?: string;
  balances: {
    current: number | null;
    available: number | null;
  };
}

interface CachedTransactionData {
  transactions: Transaction[];
  accounts: Account[];
  lastSync: string;
  period: {
    start: string;
    end: string;
  };
}

type FilterType = "all" | "income" | "spending";
type TimeFilter = "today" | "yesterday" | "week" | "month" | "3months" | "all";

// Transfer categories to exclude from smart calculations
const TRANSFER_CATEGORIES = [
  'TRANSFER_IN', 'TRANSFER_OUT', 'LOAN_PAYMENTS', 'BANK_FEES'
];

// Income categories (these count as real income, not transfers)
const INCOME_CATEGORIES = [
  'INCOME', 'PAYROLL', 'INTEREST', 'DIVIDEND', 'RENT', 'RETIREMENT'
];

const Transactions: React.FC = () => {
  const { accessToken, linkSuccess } = useContext(Context);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<string | null>(null);
  
  // Summary states
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalSpending, setTotalSpending] = useState(0);
  const [netFlow, setNetFlow] = useState(0);
  
  // Filter states
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("month");
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>(["all"]);
  const [tempSelectedAccounts, setTempSelectedAccounts] = useState<string[]>(["all"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [hideBalances, setHideBalances] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  
  // Smart vs Raw calculation mode
  const [useSmartCalculation, setUseSmartCalculation] = useState(true);
  const [smartIncome, setSmartIncome] = useState(0);
  const [smartSpending, setSmartSpending] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(`.${styles.customDropdown}`)) {
        setShowTimeDropdown(false);
        setShowAccountDropdown(false);
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load user preferences from Firestore
  const loadUserPreferences = useCallback(async () => {
    try {
      if (!auth.currentUser) return;
      
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        if (data.preferences) {
          setHideBalances(data.preferences.hideBalances || false);
          // Handle both old single account and new multi-account format
          if (data.preferences.selectedAccounts) {
            setSelectedAccounts(data.preferences.selectedAccounts);
            setTempSelectedAccounts(data.preferences.selectedAccounts);
          } else if (data.preferences.selectedAccount) {
            // Migrate old format to new format
            const accounts = data.preferences.selectedAccount === "all" 
              ? ["all"] 
              : [data.preferences.selectedAccount];
            setSelectedAccounts(accounts);
            setTempSelectedAccounts(accounts);
          }
        }
      }
    } catch (error) {
      console.error("Error loading preferences:", error);
    }
  }, []);

  // Save hide balance preference
  const toggleHideBalances = async () => {
    const newValue = !hideBalances;
    setHideBalances(newValue);
    
    try {
      if (!auth.currentUser) return;
      
      const userRef = doc(db, "users", auth.currentUser.uid);
      await setDoc(userRef, {
        preferences: {
          hideBalances: newValue,
          selectedAccounts: selectedAccounts
        }
      }, { merge: true });
    } catch (error) {
      console.error("Error saving preference:", error);
    }
  };

  // Toggle account selection (for multi-select)
  const toggleAccountSelection = (accountId: string) => {
    if (accountId === "all") {
      setTempSelectedAccounts(["all"]);
    } else {
      setTempSelectedAccounts(prev => {
        // Remove "all" if selecting specific accounts
        const withoutAll = prev.filter(id => id !== "all");
        
        // Toggle the account
        if (withoutAll.includes(accountId)) {
          const updated = withoutAll.filter(id => id !== accountId);
          // If nothing selected, default to "all"
          return updated.length === 0 ? ["all"] : updated;
        } else {
          return [...withoutAll, accountId];
        }
      });
    }
  };

  // Apply account selection
  const applyAccountSelection = async () => {
    setSelectedAccounts(tempSelectedAccounts);
    setShowAccountDropdown(false);
    
    try {
      if (!auth.currentUser) return;
      
      const userRef = doc(db, "users", auth.currentUser.uid);
      await setDoc(userRef, {
        preferences: {
          hideBalances: hideBalances,
          selectedAccounts: tempSelectedAccounts
        }
      }, { merge: true });
    } catch (error) {
      console.error("Error saving preference:", error);
    }
  };

  // Handle "All Accounts" selection (immediate)
  const handleAllAccountsSelection = async () => {
    setSelectedAccounts(["all"]);
    setTempSelectedAccounts(["all"]);
    setShowAccountDropdown(false);
    
    try {
      if (!auth.currentUser) return;
      
      const userRef = doc(db, "users", auth.currentUser.uid);
      await setDoc(userRef, {
        preferences: {
          hideBalances: hideBalances,
          selectedAccounts: ["all"]
        }
      }, { merge: true });
    } catch (error) {
      console.error("Error saving preference:", error);
    }
  };

  // Format category name (replace underscores with spaces and capitalize)
  const formatCategoryName = (category: string): string => {
    return category.replace(/_/g, ' ');
  };

  // Get unique categories from transactions
  const getUniqueCategories = (): string[] => {
    const categoriesSet = new Set<string>();
    transactions.forEach(t => {
      const category = t.personal_finance_category?.primary || t.category?.[0] || "Uncategorized";
      categoriesSet.add(category);
    });
    return Array.from(categoriesSet).sort();
  };

  // Get unique accounts that have transactions
  const getUniqueTransactionAccounts = (): Account[] => {
    const accountIds = new Set<string>();
    transactions.forEach(t => {
      accountIds.add(t.account_id);
    });
    return accounts.filter(acc => accountIds.has(acc.account_id));
  };

  // Check if cache is still fresh (less than 5 minutes old for incremental updates)
  const isCacheFresh = useCallback((lastSyncTime: string): boolean => {
    const fiveMinutesInMs = 5 * 60 * 1000;
    const lastSyncDate = new Date(lastSyncTime).getTime();
    const now = new Date().getTime();
    return (now - lastSyncDate) < fiveMinutesInMs;
  }, []);

  // Check if we need a full refresh (more than 1 hour old)
  const needsFullRefresh = useCallback((lastSyncTime: string): boolean => {
    const oneHourInMs = 1 * 60 * 60 * 1000;
    const lastSyncDate = new Date(lastSyncTime).getTime();
    const now = new Date().getTime();
    return (now - lastSyncDate) >= oneHourInMs;
  }, []);

  // Check if transaction is an internal transfer (not real income/spending)
  const isInternalTransfer = useCallback((t: Transaction): boolean => {
    const category = t.personal_finance_category?.primary || t.category?.[0] || '';
    const detailed = t.personal_finance_category?.detailed || '';
    const name = (t.name || '').toLowerCase();
    const merchant = (t.merchant_name || '').toLowerCase();
    
    // If it's an income category, it's NOT a transfer
    if (INCOME_CATEGORIES.some(ic => category.toUpperCase().includes(ic))) {
      return false;
    }
    
    // Check if it's a transfer category
    if (TRANSFER_CATEGORIES.some(tc => 
      category.toUpperCase().includes(tc.toUpperCase()) ||
      detailed.toUpperCase().includes(tc.toUpperCase())
    )) {
      return true;
    }
    
    // Check common transfer patterns in name
    const transferPatterns = [
      'transfer to', 'transfer from', 'xfer', 'payment to', 'payment from',
      'credit card payment', 'pay cc', 'online payment', 'online transfer',
      'internal transfer', 'wire transfer', 'e-transfer', 'interac'
    ];
    
    return transferPatterns.some(pattern => 
      name.includes(pattern) || merchant.includes(pattern)
    );
  }, []);

  // Calculate totals based on filtered time period
  const calculateTotals = useCallback((txns: Transaction[]) => {
    // Raw calculations (all transactions)
    let rawIncome = 0;
    let rawSpending = 0;
    
    // Smart calculations (excluding transfers, grouping refunds by merchant)
    let smartInc = 0;
    let smartSpend = 0;

    // For smart mode: Group spending transactions by merchant to net out refunds
    const merchantSpending: { [key: string]: { charges: number; refunds: number; hasPending: boolean } } = {};

    txns.forEach(t => {
      const category = t.personal_finance_category?.primary || t.category?.[0] || '';
      const isTransfer = isInternalTransfer(t);
      const isIncomeCategory = INCOME_CATEGORIES.some(ic => category.toUpperCase().includes(ic));
      
      // Raw calculation (always)
      if (t.amount < 0) {
        rawIncome += Math.abs(t.amount);
      } else {
        rawSpending += t.amount;
      }
      
      // Smart calculation
      if (!isTransfer) {
        if (t.amount < 0) {
          // Money in - check if it's real income or a refund
          if (isIncomeCategory) {
            // Real income (payroll, interest, etc.)
            smartInc += Math.abs(t.amount);
          } else {
            // Likely a refund - group with merchant
            const merchantKey = (t.merchant_name || t.name || 'Unknown').toLowerCase().trim();
            if (!merchantSpending[merchantKey]) {
              merchantSpending[merchantKey] = { charges: 0, refunds: 0, hasPending: false };
            }
            merchantSpending[merchantKey].refunds += Math.abs(t.amount);
          }
        } else {
          // Money out - spending
          const merchantKey = (t.merchant_name || t.name || 'Unknown').toLowerCase().trim();
          if (!merchantSpending[merchantKey]) {
            merchantSpending[merchantKey] = { charges: 0, refunds: 0, hasPending: false };
          }
          merchantSpending[merchantKey].charges += t.amount;
          if (t.pending) {
            merchantSpending[merchantKey].hasPending = true;
          }
        }
      }
    });

    // Calculate net spending per merchant (charges - refunds)
    Object.values(merchantSpending).forEach(group => {
      const netAmount = group.charges - group.refunds;
      if (netAmount > 0) {
        smartSpend += netAmount;
      } else if (netAmount < 0) {
        // Got more back than spent (unlikely but possible) - treat as income
        smartInc += Math.abs(netAmount);
      }
    });

    setTotalIncome(rawIncome);
    setTotalSpending(rawSpending);
    setNetFlow(rawIncome - rawSpending);
    setSmartIncome(smartInc);
    setSmartSpending(smartSpend);
  }, [isInternalTransfer]);

  // Get date range based on time filter
  const getDateRange = useCallback((filter: TimeFilter): { start: Date; end: Date } => {
    const now = new Date();
    const end = new Date(now);
    end.setHours(23, 59, 59, 999); // End of today
    let start = new Date(now);

    switch (filter) {
      case "today":
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case "yesterday":
        start.setDate(now.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end.setDate(now.getDate() - 1);
        end.setHours(23, 59, 59, 999);
        break;
      case "week":
        start.setDate(now.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        break;
      case "month":
        start.setMonth(now.getMonth() - 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "3months":
        start.setMonth(now.getMonth() - 3);
        start.setHours(0, 0, 0, 0);
        break;
      case "all":
        // Plaid allows up to 24 months
        start.setMonth(now.getMonth() - 24);
        start.setHours(0, 0, 0, 0);
        break;
    }

    return { start, end };
  }, []);

  // Get oldest transaction date from all transactions
  const getOldestTransactionDate = (): string | null => {
    if (transactions.length === 0) return null;
    
    const oldestTransaction = transactions.reduce((oldest, current) => {
      const currentDate = new Date(current.datetime || current.authorized_date || current.date);
      const oldestDate = new Date(oldest.datetime || oldest.authorized_date || oldest.date);
      return currentDate < oldestDate ? current : oldest;
    });
    
    const oldestDate = new Date(oldestTransaction.datetime || oldestTransaction.authorized_date || oldestTransaction.date);
    return oldestDate.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get date range of filtered transactions
  const getFilteredDateRange = (): string => {
    if (filteredTransactions.length === 0) return 'No transactions';
    
    if (filteredTransactions.length === 1) {
      const date = new Date(filteredTransactions[0].datetime || filteredTransactions[0].authorized_date || filteredTransactions[0].date);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    
    // Find earliest and latest dates in filtered transactions
    let earliest = new Date(filteredTransactions[0].datetime || filteredTransactions[0].authorized_date || filteredTransactions[0].date);
    let latest = new Date(filteredTransactions[0].datetime || filteredTransactions[0].authorized_date || filteredTransactions[0].date);
    
    filteredTransactions.forEach(t => {
      const date = new Date(t.datetime || t.authorized_date || t.date);
      if (date < earliest) earliest = date;
      if (date > latest) latest = date;
    });
    
    const earliestStr = earliest.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    
    const latestStr = latest.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    
    // If same date, show once
    if (earliestStr === latestStr) {
      return earliest.toLocaleDateString('en-US', { 
        weekday: 'long',
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    
    return `${earliestStr} - ${latestStr}`;
  };

  // Apply all filters
  const applyFilters = useCallback(() => {
    let filtered = [...transactions];

    // Time filter - use custom date range if enabled
    if (useCustomDateRange && customStartDate && customEndDate) {
      // Parse custom dates in local timezone at noon to avoid timezone issues
      const [startYear, startMonth, startDay] = customStartDate.split('-').map(Number);
      const start = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
      
      const [endYear, endMonth, endDay] = customEndDate.split('-').map(Number);
      const end = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);
      
      filtered = filtered.filter(t => {
        // Use the most accurate date field for filtering
        const dateToUse = t.datetime || t.authorized_date || t.date;
        // Extract date part only (YYYY-MM-DD) for comparison
        const txnDateStr = dateToUse.split('T')[0];
        const [year, month, day] = txnDateStr.split('-').map(Number);
        const txnDate = new Date(year, month - 1, day);
        txnDate.setHours(12, 0, 0, 0); // Use noon to avoid timezone issues
        
        return txnDate >= start && txnDate <= end;
      });
    } else {
      const { start, end } = getDateRange(timeFilter);
      filtered = filtered.filter(t => {
        // Use the most accurate date field for filtering
        const dateToUse = t.datetime || t.authorized_date || t.date;
        // Extract date part only (YYYY-MM-DD) for comparison
        const txnDateStr = dateToUse.split('T')[0];
        const [year, month, day] = txnDateStr.split('-').map(Number);
        const txnDate = new Date(year, month - 1, day);
        txnDate.setHours(12, 0, 0, 0); // Use noon to avoid timezone issues
        
        return txnDate >= start && txnDate <= end;
      });
    }

    // Account filter
    if (!selectedAccounts.includes("all")) {
      filtered = filtered.filter(t => selectedAccounts.includes(t.account_id));
    }

    // Category filter
    if (selectedCategory !== "all") {
      filtered = filtered.filter(t => {
        const category = t.personal_finance_category?.primary || t.category?.[0] || "Uncategorized";
        return category === selectedCategory;
      });
    }

    // Type filter (income/spending)
    if (filterType === "income") {
      filtered = filtered.filter(t => t.amount < 0); // Negative = money in
    } else if (filterType === "spending") {
      filtered = filtered.filter(t => t.amount > 0); // Positive = money out
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(query) ||
        t.merchant_name?.toLowerCase().includes(query) ||
        t.category?.some(c => c.toLowerCase().includes(query)) ||
        // Also search in personal_finance_category if category is null
        (t.personal_finance_category?.primary?.toLowerCase().includes(query)) ||
        (t.personal_finance_category?.detailed?.toLowerCase().includes(query))
      );
    }

    // Sort by date (newest first), using the most accurate timestamp available
    filtered.sort((a, b) => {
      // Use datetime if available, otherwise use authorized_datetime, otherwise fall back to date
      const aTimestamp = a.datetime || a.authorized_date || a.date;
      const bTimestamp = b.datetime || b.authorized_date || b.date;
      
      const aTime = new Date(aTimestamp).getTime();
      const bTime = new Date(bTimestamp).getTime();
      
      // If dates are equal, sort by date field as tiebreaker
      if (aTime === bTime) {
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      }
      
      return bTime - aTime;
    });

    setFilteredTransactions(filtered);
    calculateTotals(filtered);
  }, [transactions, filterType, timeFilter, selectedAccounts, selectedCategory, searchQuery, getDateRange, calculateTotals, useCustomDateRange, customStartDate, customEndDate]);

  // Filter transactions whenever filters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Save to Firestore cache
  const saveToCache = useCallback(async (newTransactions: Transaction[], accountsList: Account[]) => {
    try {
      if (!auth.currentUser) return;

      const now = new Date();
      const cacheData: CachedTransactionData = {
        transactions: newTransactions,
        accounts: accountsList,
        lastSync: now.toISOString(),
        period: {
          start: new Date(now.getFullYear(), now.getMonth() - 24, 1).toISOString(),
          end: now.toISOString()
        }
      };

      await setDoc(
        doc(db, "users", auth.currentUser.uid, "transactionCache", "latest"),
        cacheData
      );
    } catch (error) {
      console.error("Error saving to cache:", error);
    }
  }, []);

  // Fetch only recent transactions and merge with cached data
  const fetchIncrementalTransactions = useCallback(async (cachedData: CachedTransactionData) => {
    try {
      const response = await apiFetch('/api/transactions/recent', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        console.warn("Incremental fetch failed, using cached data");
        return;
      }

      const data = await response.json();
      const recentTransactions = data.latest_transactions || [];
      
      if (recentTransactions.length === 0) {
        // No new transactions, just update the lastSync time
        await saveToCache(cachedData.transactions, cachedData.accounts);
        setLastSync(new Date().toISOString());
        return;
      }

      // Merge: Remove old today/yesterday transactions and add new ones
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      const todayStr = today.toISOString().split('T')[0];
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      // Filter out old today/yesterday transactions from cache
      const olderTransactions = cachedData.transactions.filter(t => {
        const txnDate = (t.datetime || t.authorized_date || t.date).split('T')[0];
        return txnDate !== todayStr && txnDate !== yesterdayStr;
      });
      
      // Combine older cached transactions with fresh recent ones
      const mergedTransactions = [...recentTransactions, ...olderTransactions];
      
      // Sort by date descending
      mergedTransactions.sort((a, b) => {
        const aDate = new Date(a.datetime || a.authorized_date || a.date).getTime();
        const bDate = new Date(b.datetime || b.authorized_date || b.date).getTime();
        return bDate - aDate;
      });
      
      // Save merged data to cache
      await saveToCache(mergedTransactions, cachedData.accounts);
      
      // Update state
      setTransactions(mergedTransactions);
      setLastSync(new Date().toISOString());
      
      console.log(`✅ Incremental update: ${recentTransactions.length} recent transactions merged`);
    } catch (error) {
      console.error("Error in incremental fetch:", error);
    }
  }, [saveToCache]);

  // Load from Firestore cache
  const loadFromCache = useCallback(async (): Promise<CachedTransactionData | null> => {
    try {
      if (!auth.currentUser) return null;

      const cacheDoc = await getDoc(doc(db, "users", auth.currentUser.uid, "transactionCache", "latest"));
      
      if (cacheDoc.exists()) {
        return cacheDoc.data() as CachedTransactionData;
      }
    } catch (error) {
      console.error("Error loading from cache:", error);
    }
    return null;
  }, []);

  // Fetch from API and cache to Firestore
  const fetchAndCacheTransactionData = useCallback(async () => {
    // Fetch transactions
    const txnResponse = await apiFetch('/api/transactions', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!txnResponse.ok) {
      let errorMessage = "Failed to fetch transactions";
      try {
        const errData = await txnResponse.json();
        const code = errData?.error?.error_code;
        if (code === 'NO_ACCOUNTS' || code === 'ITEM_LOGIN_REQUIRED' || code === 'INVALID_ACCESS_TOKEN') {
          errorMessage = 'Your bank connection needs to be refreshed. Please reconnect your bank.';
        } else if (errData?.error?.display_message) {
          errorMessage = errData.error.display_message;
        } else if (typeof errData?.error === 'string') {
          errorMessage = errData.error;
        }
      } catch { /* keep default */ }
      throw new Error(errorMessage);
    }

    const txnData = await txnResponse.json();
    
    // Fetch accounts
    const acctResponse = await apiFetch('/api/accounts', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    let accountsData: Account[] = [];
    if (acctResponse.ok) {
      const acctResult = await acctResponse.json();
      accountsData = acctResult.accounts || [];
      
      // Also save to shared accounts cache for Bills and other pages
      if (auth.currentUser) {
        await setDoc(
          doc(db, "users", auth.currentUser.uid, "accountsCache", "latest"),
          {
            accounts: accountsData,
            lastSync: new Date().toISOString()
          }
        );
      }
    }

    const allTransactions = txnData.latest_transactions || [];
    
    // Save to cache
    await saveToCache(allTransactions, accountsData);
    
    // Update state
    setTransactions(allTransactions);
    setAccounts(accountsData);
    setLastSync(new Date().toISOString());
  }, [saveToCache]);

  // Load transaction data - First from cache, then smart incremental refresh
  const loadTransactionData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      if (!auth.currentUser) {
        throw new Error("Not authenticated");
      }

      // Try to load from Firestore cache first
      const cachedData = await loadFromCache();
      
      if (cachedData) {
        // Always show cached data first for better UX
        setTransactions(cachedData.transactions);
        setAccounts(cachedData.accounts || []);
        setLastSync(cachedData.lastSync);
        
        if (forceRefresh) {
          // Force refresh - fetch all transactions
          console.log("🔄 Force refresh - fetching all transactions");
          setLoading(false);
          setRefreshing(true);
          await fetchAndCacheTransactionData();
          setRefreshing(false);
          return;
        }
        
        if (isCacheFresh(cachedData.lastSync)) {
          // Cache is less than 5 minutes old - use it as-is
          console.log("✅ Using fresh cache data (< 5 min old)");
          setLoading(false);
          return;
        }
        
        if (!needsFullRefresh(cachedData.lastSync)) {
          // Cache is 5 min - 1 hour old - do incremental update (today's transactions only)
          console.log("🔄 Incremental update - fetching today's transactions");
          setLoading(false);
          await fetchIncrementalTransactions(cachedData);
          return;
        }
        
        // Cache is more than 1 hour old - do full refresh
        console.log("🔄 Full refresh - cache is stale (> 1 hour)");
        setLoading(false);
        setRefreshing(true);
        await fetchAndCacheTransactionData();
        setRefreshing(false);
        return;
      }

      // No cache at all - fetch everything
      console.log("📥 No cache found - fetching all transactions");
      await fetchAndCacheTransactionData();
      
    } catch (error) {
      console.error("Error loading transactions:", error);
      setError(error instanceof Error ? error.message : "Failed to load transaction data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchAndCacheTransactionData, fetchIncrementalTransactions, isCacheFresh, loadFromCache, needsFullRefresh]);

  useEffect(() => {
    if (linkSuccess && accessToken) {
      loadTransactionData();
      loadUserPreferences();
    }
  }, [linkSuccess, accessToken, loadTransactionData, loadUserPreferences]);

  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadTransactionData(true);
    setRefreshing(false);
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Math.abs(amount));
  };

  // Format date
  const formatDate = (dateStr: string) => {
    // Handle both ISO 8601 timestamps and date-only strings
    let date: Date;
    const hasTime = dateStr.includes('T');
    
    if (hasTime) {
      // Has time component (e.g., "2025-12-18T20:59:04.582Z")
      date = new Date(dateStr);
    } else {
      // Date only (e.g., "2025-12-18") - parse in local timezone
      const [year, month, day] = dateStr.split('-').map(Number);
      date = new Date(year, month - 1, day);
    }
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Format based on whether time is available
    let formattedDateTime: string;
    
    if (hasTime) {
      // Show date and time
      formattedDateTime = date.toLocaleString('en-US', { 
        year: 'numeric',
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else {
      // Show date only
      formattedDateTime = date.toLocaleDateString('en-US', { 
        year: 'numeric',
        month: 'short', 
        day: 'numeric'
      });
    }
    
    // Add prefix for today or yesterday
    if (date.toDateString() === today.toDateString()) {
      return `(Today) ${formattedDateTime}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `(Yesterday) ${formattedDateTime}`;
    }
    
    return formattedDateTime;
  };

  // Get account name by ID
  const getAccountName = (accountId: string) => {
    const account = accounts.find(a => a.account_id === accountId);
    return account?.name || account?.official_name || "Unknown Account";
  };

  // Get account color class based on account ID (same pattern as Bills)
  const getAccountColorById = (accountId: string): string => {
    const accountIndex = accounts.findIndex(acc => acc.account_id === accountId);
    return `color${accountIndex >= 0 ? accountIndex % 6 : 0}`;
  };

  // Time filter options
  const timeFilterOptions: { value: TimeFilter; label: string }[] = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "3months", label: "Last 3 Months" },
    { value: "all", label: "All Time" }
  ];

  if (loading) {
    return (
      <div className={styles.transactions}>
        <div className={styles.loadingState}>
          <RefreshCw className={styles.spinner} size={32} />
          <p>Loading transactions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.transactions}>
        <div className={styles.errorState}>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={() => loadTransactionData(true)} className={styles.retryBtn}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.transactions}>
      {/* Header with Balance Toggle */}
      <div className={styles.transactionHeader}>
        <h2>Financial Summary</h2>
        <div className={styles.headerActions}>
          {/* Smart/Raw Toggle */}
          <div className={styles.calculationToggle}>
            <button 
              className={`${styles.toggleOption} ${useSmartCalculation ? styles.active : ''}`}
              onClick={() => setUseSmartCalculation(true)}
              title="Excludes internal transfers and applies refunds. Shows your true income and spending."
            >
              Smart
            </button>
            <button 
              className={`${styles.toggleOption} ${!useSmartCalculation ? styles.active : ''}`}
              onClick={() => setUseSmartCalculation(false)}
              title="Shows all raw transaction amounts without any adjustments."
            >
              Raw
            </button>
          </div>
          <button onClick={toggleHideBalances} className={styles.toggleBalanceBtn} title={hideBalances ? "Show balances" : "Hide balances"}>
            {hideBalances ? <EyeOff size={20} /> : <Eye size={20} />}
            <span>{hideBalances ? "Show" : "Hide"}</span>
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className={styles.summaryGrid}>
        <div className={`${styles.summaryCard} ${styles.incomeCard}`}>
          <div className={styles.cardIcon}>
            <ArrowDownLeft size={24} />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{useSmartCalculation ? 'Income' : 'Money In'}</span>
            <span className={styles.cardValue}>
              {hideBalances ? "••••••" : formatCurrency(useSmartCalculation ? smartIncome : totalIncome)}
            </span>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.spendingCard}`}>
          <div className={styles.cardIcon}>
            <ArrowUpRight size={24} />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{useSmartCalculation ? 'Spending' : 'Money Out'}</span>
            <span className={styles.cardValue}>
              {hideBalances ? "••••••" : formatCurrency(useSmartCalculation ? smartSpending : totalSpending)}
            </span>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${(useSmartCalculation ? (smartIncome - smartSpending) : netFlow) >= 0 ? styles.positiveCard : styles.negativeCard}`}>
          <div className={styles.cardIcon}>
            {(useSmartCalculation ? (smartIncome - smartSpending) : netFlow) >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{useSmartCalculation ? 'Saved' : 'Net Flow'}</span>
            <span className={styles.cardValue}>
              {hideBalances ? "••••••" : (() => {
                const flow = useSmartCalculation ? (smartIncome - smartSpending) : netFlow;
                return `${flow >= 0 ? "+" : "-"}${formatCurrency(Math.abs(flow))}`;
              })()}
            </span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className={styles.filterSection}>
        <div className={styles.mainFilters}>
          {/* Type Filter Tabs */}
          <div className={styles.typeFilters}>
            <button 
              className={`${styles.filterTab} ${filterType === "all" ? styles.active : ""}`}
              onClick={() => setFilterType("all")}
            >
              All
            </button>
            <button 
              className={`${styles.filterTab} ${filterType === "income" ? styles.active : ""}`}
              onClick={() => setFilterType("income")}
            >
              <ArrowDownLeft size={16} />
              Income
            </button>
            <button 
              className={`${styles.filterTab} ${filterType === "spending" ? styles.active : ""}`}
              onClick={() => setFilterType("spending")}
            >
              <ArrowUpRight size={16} />
              Spending
            </button>
          </div>

          {/* Time Filter Dropdown - Custom */}
          <div className={styles.customDropdown}>
            <button 
              className={styles.dropdownTrigger}
              onClick={() => {
                setShowTimeDropdown(!showTimeDropdown);
                setShowAccountDropdown(false);
                setShowCategoryDropdown(false);
              }}
            >
              <Calendar size={20} />
              <span>{timeFilterOptions.find(o => o.value === timeFilter)?.label}</span>
              <ChevronDown size={18} className={showTimeDropdown ? styles.rotated : ''} />
            </button>
            
            {showTimeDropdown && (
              <div className={styles.dropdownMenu}>
                {timeFilterOptions.map(opt => (
                  <button
                    key={opt.value}
                    className={`${styles.dropdownItem} ${timeFilter === opt.value ? styles.active : ''}`}
                    onClick={() => {
                      setTimeFilter(opt.value);
                      setShowTimeDropdown(false);
                    }}
                  >
                    {opt.label}
                    {timeFilter === opt.value && <span className={styles.checkmark}>✓</span>}
                  </button>
                ))}
                {getOldestTransactionDate() && (
                  <div className={styles.dropdownInfo}>
                    <span>Oldest transaction: {getOldestTransactionDate()}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Category Selector - Custom Dropdown */}
          <div className={styles.customDropdown}>
            <button 
              className={`${styles.dropdownTrigger} ${styles.categoryTrigger}`}
              onClick={() => {
                setShowCategoryDropdown(!showCategoryDropdown);
                setShowTimeDropdown(false);
                setShowAccountDropdown(false);
              }}
            >
              <Tag size={20} />
              <span>
                {selectedCategory === "all" ? "All Categories" : formatCategoryName(selectedCategory)}
              </span>
              <ChevronDown size={18} className={showCategoryDropdown ? styles.rotated : ''} />
            </button>
            
            {showCategoryDropdown && (
              <div className={styles.dropdownMenu}>
                <button
                  className={`${styles.dropdownItem} ${selectedCategory === "all" ? styles.active : ''}`}
                  onClick={() => {
                    setSelectedCategory("all");
                    setShowCategoryDropdown(false);
                  }}
                >
                  <Tag size={18} />
                  <span>All Categories</span>
                  {selectedCategory === "all" && <span className={styles.checkmark}>✓</span>}
                </button>
                {getUniqueCategories().map(cat => (
                  <button
                    key={cat}
                    className={`${styles.dropdownItem} ${selectedCategory === cat ? styles.active : ''}`}
                    onClick={() => {
                      setSelectedCategory(cat);
                      setShowCategoryDropdown(false);
                    }}
                  >
                    <Tag size={18} />
                    <span>{formatCategoryName(cat)}</span>
                    {selectedCategory === cat && <span className={styles.checkmark}>✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Account Selector - Custom Dropdown with Multi-Select */}
          {accounts.length > 1 && (
            <div className={styles.customDropdown}>
              <button 
                className={`${styles.dropdownTrigger} ${styles.accountTrigger}`}
                onClick={() => {
                  setShowAccountDropdown(!showAccountDropdown);
                  setShowTimeDropdown(false);
                  setShowCategoryDropdown(false);
                  // Reset temp selection to current when opening
                  if (!showAccountDropdown) {
                    setTempSelectedAccounts(selectedAccounts);
                  }
                }}
              >
                <Wallet size={20} />
                <span>
                  {selectedAccounts.includes("all") 
                    ? "All Accounts" 
                    : selectedAccounts.length === 1
                      ? accounts.find(a => a.account_id === selectedAccounts[0])?.name || "Select Account"
                      : `${selectedAccounts.length} Accounts`}
                </span>
                <ChevronDown size={18} className={showAccountDropdown ? styles.rotated : ''} />
              </button>
              
              {showAccountDropdown && (
                <div className={styles.dropdownMenu} data-dropdown="filter">
                  <button
                    className={`${styles.dropdownItem} ${tempSelectedAccounts.includes("all") ? styles.active : ''}`}
                    onClick={handleAllAccountsSelection}
                  >
                    <Wallet size={18} />
                    <span>All Accounts</span>
                    {tempSelectedAccounts.includes("all") && <span className={styles.checkmark}>✓</span>}
                  </button>
                  {getUniqueTransactionAccounts().map(acc => {
                    const colorClass = getAccountColorById(acc.account_id);
                    return (
                      <div
                        key={acc.account_id}
                        className={styles.accountItemWithCheckbox}
                        onClick={() => toggleAccountSelection(acc.account_id)}
                      >
                        <div className={`${styles.accountCheckbox} ${tempSelectedAccounts.includes(acc.account_id) ? styles.checked : ''}`}>
                          {tempSelectedAccounts.includes(acc.account_id) && <Check size={14} />}
                        </div>
                        <span className={`${styles.accountDropdownBadge} ${styles[colorClass]}`}>
                          {acc.name} {acc.mask ? `(••••${acc.mask})` : ''}
                        </span>
                      </div>
                    );
                  })}
                  {!tempSelectedAccounts.includes("all") && tempSelectedAccounts.length > 0 && (
                    <button
                      className={`${styles.dropdownItem} ${styles.confirmButton}`}
                      onClick={applyAccountSelection}
                    >
                      <span>Apply Selection</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className={styles.filterActions}>
            <button 
              className={styles.filterToggle}
              onClick={() => setShowFilters(!showFilters)}
              title="Toggle advanced filters"
            >
              <Filter size={18} />
            </button>
            <button 
              className={styles.refreshBtn} 
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh data"
            >
              <RefreshCw size={18} className={refreshing ? styles.spinning : ""} />
            </button>
          </div>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className={styles.expandedFilters}>
            {/* Search */}
            <div className={styles.searchWrapper}>
              <Search size={22} />
              <input
                type="text"
                placeholder="Search transactions by name, merchant, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
                autoFocus
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className={styles.clearSearch}>
                  <X size={18} />
                </button>
              )}
            </div>

            {/* Date Range Filter */}
            <div className={styles.dateRangeWrapper}>
              {getOldestTransactionDate() && (
                <div className={styles.dateRangeInfo}>
                  <span>Available data from {getOldestTransactionDate()}</span>
                </div>
              )}
              <div className={styles.dateInputs}>
                <div className={styles.dateInputGroup}>
                  <label>From</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className={styles.dateInput}
                  />
                </div>
                <div className={styles.dateInputGroup}>
                  <label>To</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className={styles.dateInput}
                  />
                </div>
                <button 
                  className={styles.applyDateRangeBtn}
                  onClick={() => setUseCustomDateRange(true)}
                  disabled={!customStartDate || !customEndDate}
                >
                  <Search size={18} />
                  Apply Date Range
                </button>
                {useCustomDateRange && (
                  <button 
                    className={styles.clearDateRangeBtn}
                    onClick={() => {
                      setUseCustomDateRange(false);
                      setCustomStartDate("");
                      setCustomEndDate("");
                    }}
                  >
                    <X size={18} />
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Date Range and Last Sync Info */}
      {lastSync && (
        <div className={styles.syncInfoBar}>
          <p className={styles.currentDate}>
            {getFilteredDateRange()}
            {filteredTransactions.length > 0 && (
              <span className={styles.transactionCountInline}>
                {" • "}{filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}
              </span>
            )}
          </p>
          <p className={styles.lastSync}>
            Last updated: {new Date(lastSync).toLocaleString()}
          </p>
        </div>
      )}

      {/* Transactions List */}
      <div className={styles.transactionsList}>
        {filteredTransactions.length === 0 ? (
          <div className={styles.emptyState}>
            <Wallet size={48} />
            <p>No transactions found</p>
            <span>Try adjusting your filters</span>
          </div>
        ) : (
          <>
            {filteredTransactions.map((txn, index) => {
              // Use the most accurate date field for grouping (same as sorting and display)
              const getMostAccurateDate = (transaction: Transaction) => {
                return transaction.datetime || transaction.authorized_date || transaction.date;
              };
              
              // Check if we need to show a date separator
              const currentDateStr = getMostAccurateDate(txn).split('T')[0]; // Get YYYY-MM-DD only
              const previousDateStr = index > 0 ? getMostAccurateDate(filteredTransactions[index - 1]).split('T')[0] : null;
              const showDateSeparator = index === 0 || currentDateStr !== previousDateStr;

              // Format the date separator label
              const getDateSeparatorLabel = (dateStr: string) => {
                const dateOnly = dateStr.split('T')[0]; // Get YYYY-MM-DD
                const [year, month, day] = dateOnly.split('-').map(Number);
                const date = new Date(year, month - 1, day); // Create in local timezone
                
                const today = new Date();
                const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                const yesterdayStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;

                const fullDate = date.toLocaleDateString('en-US', { 
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long', 
                  day: 'numeric'
                });

                if (dateOnly === todayStr) {
                  return `(Today) ${fullDate}`;
                } else if (dateOnly === yesterdayStr) {
                  return `(Yesterday) ${fullDate}`;
                } else {
                  return fullDate;
                }
              };

              return (
                <React.Fragment key={txn.id}>
                  {showDateSeparator && (
                    <div className={styles.dateSeparator}>
                      <span className={styles.dateSeparatorLabel}>
                        {getDateSeparatorLabel(getMostAccurateDate(txn))}
                      </span>
                    </div>
                  )}
                  <div className={styles.transactionItem}>
                    <div className={`${styles.txnIcon} ${txn.amount < 0 ? styles.income : styles.spending}`}>
                      {txn.amount < 0 ? <ArrowDownLeft size={20} /> : <ArrowUpRight size={20} />}
                    </div>
                    
                    <div className={styles.txnDetails}>
                      <span className={styles.txnName}>
                        {txn.merchant_name || txn.name}
                        {txn.pending && <span className={styles.pendingBadge}>Pending</span>}
                      </span>
                      <span className={styles.txnMeta}>
                        {txn.category?.[0] || 
                         txn.personal_finance_category?.primary?.replace(/_/g, ' ') || 
                         "Uncategorized"}
                      </span>
                    </div>
                    
                    <span className={`${styles.accountBadge} ${styles[getAccountColorById(txn.account_id)]}`}>
                      {getAccountName(txn.account_id)}
                    </span>

                    <div className={styles.txnRight}>
                      <span className={`${styles.txnAmount} ${txn.amount < 0 ? styles.income : styles.spending}`}>
                        {hideBalances ? "••••" : `${txn.amount < 0 ? "+" : "-"}${formatCurrency(txn.amount)}`}
                      </span>
                      <span className={styles.txnDate}>{formatDate(txn.datetime || txn.authorized_date || txn.date)}</span>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default Transactions;
